import { t } from 'i18next';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { FormSubmissionResTypes } from '#/client';
import { noop, noopPromise, techSearchParams } from '#/service/utils';
import { authActions, useAuthentication } from '#/store/auth';
import { generalModalStoreActions } from '#/store/modals';

interface AutoLoginToken {
  token: string;
}
interface AutoLogin {
  isAutoLogin: boolean;
  autoLoginToken: { token: string };
  removeAutoLoginParam: () => void;
}

const parseAutoLoginParam = (
  isAutoLogin: boolean,
  queryParam: string
): AutoLoginToken => {
  let autoLoginToken = { token: '' };

  if (isAutoLogin) {
    try {
      autoLoginToken = JSON.parse(
        decodeURIComponent(escape(window.atob(queryParam)))
      );
    } catch (err) {
      console.error('parseAutoLoginParam', err);
    }
  }

  return autoLoginToken;
};

export const useInitAutoLogin = () => {
  const { isInitialised, isLoggedIn } = useAuthentication();
  const { isAutoLogin, removeAutoLoginParam } = useAutoLogin();

  if (!isInitialised || !isAutoLogin) {
    return;
  }

  if (isLoggedIn) {
    removeAutoLoginParam();
  } else {
    authActions.showLogin();
  }
};

export const useSubmitAutoLogin = (submit: typeof noopPromise) => {
  const { isAutoLogin, autoLoginToken, removeAutoLoginParam } = useAutoLogin();

  return useEffect(() => {
    if (isAutoLogin) {
      submit(autoLoginToken)
        .then((res) => {
          if (res._meta.type === FormSubmissionResTypes.ERROR) {
            const message = res._meta.message || t('error.generic.message');

            generalModalStoreActions.showModal({
              buttonLabel: t('button.ok'),
              message: message,
              onDismiss: () => authActions.hideLogin(),
            });
          }
        })
        .catch((error) => {
          console.error('Auto login failed', { error });
        })
        .then(removeAutoLoginParam)
        .catch(noop);
    }
  }, [autoLoginToken, isAutoLogin, removeAutoLoginParam, submit]);
};

export const useAutoLogin = (): AutoLogin => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const autoLoginParam = searchParams.get('autoLogin') || '';

  const isAutoLogin = !!autoLoginParam;

  const autoLoginToken = parseAutoLoginParam(isAutoLogin, autoLoginParam);

  const removeAutoLoginParam = useCallback(() => {
    navigate(
      {
        pathname: pathname,
        search: techSearchParams(search),
      },
      { replace: true }
    );
  }, [navigate, pathname, search]);

  return {
    isAutoLogin,
    autoLoginToken,
    removeAutoLoginParam,
  };
};
